import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiCopy, FiKey, FiRefreshCw, FiShield, FiTrash2, FiX } from 'react-icons/fi';
import { api } from '../../services/api';
import { formatLocalDate } from '../../utils/formatters';
import { SCOPE_OPTIONS } from './scopeLabels';

const SKILL_ID = 'polysports-trading-agent';

const DEFAULT_FORM = {
  name: '',
  description: '',
  expiresInDays: '180',
  maxOrderNotional: '',
  dailyOrderCount: '',
};

function buildRiskLimits(form) {
  const riskLimits = {};

  if (form.maxOrderNotional !== '') {
    riskLimits.max_order_notional_usdc = Number(form.maxOrderNotional);
  }

  if (form.dailyOrderCount !== '') {
    riskLimits.daily_order_count = Number(form.dailyOrderCount);
  }

  return Object.keys(riskLimits).length > 0 ? riskLimits : null;
}

function getStatusTone(status) {
  if (status === 'active') {
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
  }
  if (status === 'revoked') {
    return 'border-rose-500/30 bg-rose-500/10 text-rose-300';
  }
  return 'border-amber-500/30 bg-amber-500/10 text-amber-200';
}

function maskKey(prefix) {
  if (!prefix) return '-';
  return `${prefix}********`;
}

function OverlayCard({ children, zIndexClass = 'z-[150]' }) {
  return (
    <div className={`fixed inset-0 ${zIndexClass} flex items-center justify-center bg-black/70 backdrop-blur-sm px-4`}>
      {children}
    </div>
  );
}

function CreateKeyModal({
  open,
  form,
  submitting,
  onChange,
  onClose,
  onSubmit,
  t,
}) {
  if (!open) return null;

  return (
    <OverlayCard>
      <div className="w-full max-w-2xl rounded-[28px] border border-white/10 bg-gradient-to-br from-surface/95 via-surface to-surface-highlight/70 shadow-[0_40px_120px_rgba(2,6,23,0.65)] ring-1 ring-white/5">
        <div className="flex items-start justify-between gap-4 border-b border-white/5 px-6 py-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
              {t('account.apiKeys.eyebrow')}
            </div>
            <h3 className="mt-2 text-xl font-semibold text-white">
              {t('account.apiKeys.createTitle')}
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              {t('account.apiKeys.createModalDesc')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            <FiX size={16} />
          </button>
        </div>

        <div className="space-y-5 px-6 py-6">
          <div className="rounded-2xl border border-accent/20 bg-gradient-to-r from-accent/10 via-sky-500/10 to-emerald-500/10 px-4 py-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <FiShield className="text-accent-soft" />
              {t('account.apiKeys.fullAccessTitle')}
            </div>
            <p className="mt-2 text-sm text-slate-300">
              {t('account.apiKeys.fullAccessDesc')}
            </p>
          </div>

          <div className="grid gap-4">
            <div>
              <label className="mb-1 block text-xs text-gray-400">
                {t('account.apiKeys.nameLabel')}
              </label>
              <input
                value={form.name}
                onChange={(event) => onChange('name', event.target.value)}
                placeholder={t('account.apiKeys.namePlaceholder')}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-400">
                {t('account.apiKeys.descriptionLabel')}
              </label>
              <textarea
                value={form.description}
                onChange={(event) => onChange('description', event.target.value)}
                placeholder={t('account.apiKeys.descriptionPlaceholder')}
                rows={3}
                className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs text-gray-400">
                  {t('account.apiKeys.expiryLabel')}
                </label>
                <input
                  value={form.expiresInDays}
                  onChange={(event) => onChange('expiresInDays', event.target.value)}
                  inputMode="numeric"
                  placeholder="180"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-400">
                  {t('account.apiKeys.maxOrderLabel')}
                </label>
                <input
                  value={form.maxOrderNotional}
                  onChange={(event) => onChange('maxOrderNotional', event.target.value)}
                  inputMode="decimal"
                  placeholder="50"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-400">
                  {t('account.apiKeys.dailyLimitLabel')}
                </label>
                <input
                  value={form.dailyOrderCount}
                  onChange={(event) => onChange('dailyOrderCount', event.target.value)}
                  inputMode="numeric"
                  placeholder="10"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-white/5 px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-strong via-accent to-accent-soft px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(63,136,255,0.25)] transition-colors hover:from-accent hover:to-accent-strong disabled:opacity-60"
          >
            {submitting ? <FiRefreshCw className="animate-spin" /> : <FiKey />}
            {submitting ? t('common.processing') : t('account.apiKeys.createAction')}
          </button>
        </div>
      </div>
    </OverlayCard>
  );
}

function IssuedKeyModal({ issuedKey, copied, onCopy, onClose, t }) {
  if (!issuedKey?.key) return null;

  return (
    <OverlayCard zIndexClass="z-[155]">
      <div className="w-full max-w-2xl rounded-[28px] border border-white/10 bg-gradient-to-br from-surface/95 via-surface to-surface-highlight/70 shadow-[0_40px_120px_rgba(2,6,23,0.65)] ring-1 ring-white/5">
        <div className="flex items-start justify-between gap-4 border-b border-white/5 px-6 py-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
              {t('account.apiKeys.eyebrow')}
            </div>
            <h3 className="mt-2 text-xl font-semibold text-white">
              {t('account.apiKeys.issuedTitle')}
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              {t('account.apiKeys.issuedHint')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            <FiX size={16} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-6">
          <div className="rounded-2xl border border-accent/30 bg-gradient-to-r from-accent/10 via-sky-500/10 to-emerald-500/10 p-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
              {t('account.apiKeys.nameLabel')}
            </div>
            <div className="mt-2 text-base font-semibold text-white">{issuedKey.name}</div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4 font-mono text-sm break-all text-emerald-200">
            {issuedKey.key}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-slate-300">
            <div className="font-medium text-white">{t('account.apiKeys.keyVisibilityTitle')}</div>
            <div className="mt-1 text-slate-400">{t('account.apiKeys.keyVisibilityDesc')}</div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-white/5 px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-strong via-accent to-accent-soft px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(63,136,255,0.25)] transition-colors hover:from-accent hover:to-accent-strong"
          >
            {copied ? <FiCheck /> : <FiCopy />}
            {copied ? t('account.apiKeys.copied') : t('account.apiKeys.copy')}
          </button>
        </div>
      </div>
    </OverlayCard>
  );
}

export function ApiKeysPanel({ active, showHeader = true }) {
  const { t, i18n } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [issuedKey, setIssuedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [workingKeyId, setWorkingKeyId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [form, setForm] = useState(() => ({ ...DEFAULT_FORM }));

  useEffect(() => {
    if (!active) return;
    loadKeys();
  }, [active]);

  const loadKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.listUserApiKeys();
      if (res.code !== 'SUCCESS') {
        throw new Error(res.msg || t('account.apiKeys.loadFailed'));
      }
      setKeys(res.data?.items || []);
    } catch (err) {
      setError(err.message || t('account.apiKeys.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value, options) => {
    if (!value) return '-';
    return formatLocalDate(
      value,
      options,
      i18n.language
    );
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({ ...DEFAULT_FORM });
  };

  const openCreateModal = () => {
    setError('');
    setNotice('');
    resetForm();
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (submitting) return;
    setCreateModalOpen(false);
    resetForm();
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setError(t('account.apiKeys.nameRequired'));
      return;
    }

    setSubmitting(true);
    setError('');
    setNotice('');

    try {
      const res = await api.createUserApiKey({
        name: form.name.trim(),
        description: form.description.trim() || null,
        scopes: [...SCOPE_OPTIONS],
        allowed_skill_ids: [SKILL_ID],
        risk_limits: buildRiskLimits(form),
        expires_in_days: form.expiresInDays ? Number(form.expiresInDays) : undefined,
      });

      if (res.code !== 'SUCCESS') {
        throw new Error(res.msg || t('account.apiKeys.createFailed'));
      }

      setIssuedKey({
        key: res.data?.plain_text_key,
        name: res.data?.name,
        keyId: res.data?.key_id,
      });
      setCopied(false);
      setNotice(t('account.apiKeys.createdSuccess'));
      setCreateModalOpen(false);
      resetForm();
      await loadKeys();
    } catch (err) {
      setError(err.message || t('account.apiKeys.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyIssuedKey = async () => {
    if (!issuedKey?.key) return;
    try {
      await navigator.clipboard.writeText(issuedKey.key);
      setCopied(true);
      setNotice(t('account.apiKeys.copySuccess'));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('account.apiKeys.copyFailed'));
    }
  };

  const handleRotate = async (keyId) => {
    if (!window.confirm(t('account.apiKeys.rotateConfirm'))) {
      return;
    }
    setWorkingKeyId(keyId);
    setError('');
    setNotice('');
    try {
      const res = await api.rotateUserApiKey(keyId);
      if (res.code !== 'SUCCESS') {
        throw new Error(res.msg || t('account.apiKeys.rotateFailed'));
      }
      setIssuedKey({
        key: res.data?.plain_text_key,
        name: res.data?.name,
        keyId: res.data?.key_id,
      });
      setCopied(false);
      setNotice(t('account.apiKeys.rotatedSuccess'));
      await loadKeys();
    } catch (err) {
      setError(err.message || t('account.apiKeys.rotateFailed'));
    } finally {
      setWorkingKeyId(null);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm(t('account.apiKeys.revokeConfirm'))) {
      return;
    }
    setWorkingKeyId(keyId);
    setError('');
    setNotice('');
    try {
      const res = await api.revokeUserApiKey(keyId, 'user_revoked');
      if (res.code !== 'SUCCESS') {
        throw new Error(res.msg || t('account.apiKeys.revokeFailed'));
      }
      setNotice(t('account.apiKeys.revokedSuccess'));
      await loadKeys();
    } catch (err) {
      setError(err.message || t('account.apiKeys.revokeFailed'));
    } finally {
      setWorkingKeyId(null);
    }
  };

  return (
    <>
      <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-surface-highlight/60 via-surface/90 to-surface-highlight/30 p-6 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
        {showHeader && (
          <div className="mb-6 flex items-start gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-violet-400/20 bg-violet-500/10">
                <FiKey className="text-violet-300" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  {t('account.apiKeys.eyebrow')}
                </div>
                <h2 className="mt-1 text-lg font-semibold text-white">
                  {t('account.apiKeys.title')}
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  {t('account.apiKeys.description')}
                </p>
              </div>
            </div>
          </div>
        )}

        {notice && (
          <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            {notice}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {error}
          </div>
        )}

        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-sm font-semibold text-white">
              {t('account.apiKeys.activeTitle')}
            </div>
            <p className="mt-1 text-sm text-slate-400">
              {t('account.apiKeys.activeDesc')}
            </p>
          </div>

          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-strong via-accent to-accent-soft px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(63,136,255,0.25)] transition-colors hover:from-accent hover:to-accent-strong"
          >
            <FiKey />
            {t('account.apiKeys.createAction')}
          </button>
        </div>

        <div className="overflow-hidden rounded-[24px] border border-white/10 bg-black/10">
          <div className="border-b border-white/5 px-4 py-3 text-sm text-slate-400">
            {t('account.apiKeys.listHint')}
          </div>

          {loading ? (
            <div className="px-4 py-10 text-sm text-slate-400">{t('common.loading')}</div>
          ) : keys.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="text-sm text-slate-400">{t('account.apiKeys.empty')}</div>
              <button
                type="button"
                onClick={openCreateModal}
                className="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white transition-colors hover:bg-white/10"
              >
                <FiKey />
                {t('account.apiKeys.createAction')}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[760px] w-full text-left">
                <thead className="bg-white/[0.03] text-xs uppercase tracking-[0.18em] text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">{t('account.apiKeys.columns.name')}</th>
                    <th className="px-4 py-3 font-medium">{t('account.apiKeys.columns.key')}</th>
                    <th className="px-4 py-3 font-medium">{t('account.apiKeys.columns.created')}</th>
                    <th className="px-4 py-3 font-medium">{t('account.apiKeys.columns.lastUsed')}</th>
                    <th className="px-4 py-3 text-right font-medium">{t('account.apiKeys.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {keys.map((item) => {
                    const isWorking = workingKeyId === item.key_id;
                    const isActive = item.status === 'active';

                    return (
                      <tr key={item.key_id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="text-sm font-semibold text-white">{item.name}</div>
                            <span className={`rounded-full border px-2 py-1 text-[11px] uppercase tracking-[0.16em] ${getStatusTone(item.status)}`}>
                              {t(`account.apiKeys.status.${item.status}`)}
                            </span>
                          </div>
                          {item.description && (
                            <div className="mt-1 text-sm text-slate-400">{item.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-4 font-mono text-sm text-slate-300">
                          {maskKey(item.key_prefix)}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-300">
                          {formatDate(item.created_at, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-300">
                          {formatDate(item.last_used_at, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleRotate(item.key_id)}
                              disabled={!isActive || isWorking}
                              title={t('account.apiKeys.rotate')}
                              aria-label={t('account.apiKeys.rotate')}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-slate-300 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
                            >
                              <FiRefreshCw className={isWorking ? 'animate-spin' : ''} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRevoke(item.key_id)}
                              disabled={!isActive || isWorking}
                              title={t('account.apiKeys.revoke')}
                              aria-label={t('account.apiKeys.revoke')}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-500/20 text-rose-200 transition-colors hover:bg-rose-500/10 disabled:opacity-40"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <CreateKeyModal
        open={createModalOpen}
        form={form}
        submitting={submitting}
        onChange={handleFieldChange}
        onClose={closeCreateModal}
        onSubmit={handleCreate}
        t={t}
      />

      <IssuedKeyModal
        issuedKey={issuedKey}
        copied={copied}
        onCopy={handleCopyIssuedKey}
        onClose={() => setIssuedKey(null)}
        t={t}
      />
    </>
  );
}
